/**
 * The CASCADING RESOLUTION RULE — what happens to a breadcrumb when the user
 * switches context somewhere in the middle of it.
 *
 * A breadcrumb is an ordered CHAIN: each node is meaningful only under the nodes
 * before it. »Werte« is a step of *this* workshop; the workshop belongs to *this*
 * stage. So when a person opens the dropdown of one crumb and picks a sibling,
 * every FOLLOWING crumb has to be asked again, **starting from the outermost**:
 *
 *   – does it still resolve under the new context? → it STAYS, and the deepest
 *     surviving node becomes the new target route (the person keeps their place);
 *   – it does not? → it is DROPPED, and so is everything after it: the chain is
 *     ORDERED, and a node whose parent is gone has no context left to resolve in.
 *     A node that *would* have resolved on its own still goes — it was never
 *     addressable on its own.
 *
 * In the worst case every following node drops and the chain degrades exactly to
 * the level that was switched — never further, never to a broken deep link.
 *
 * The one thing this module cannot know is whether a given node exists under a
 * given context: that is the host application's knowledge (its routes, its data).
 * It arrives as a {@link ScopeResolver} — data in, data out — so the rule itself
 * stays pure and testable, and career and DNA feed the SAME rule.
 */

/**
 * One addressable position in a scope chain.
 *
 * `key` is the LEVEL identity — it stays the same across contexts (»the workshop
 * level«, »the step level«), which is what lets a resolver recognise what it is
 * being asked about. `id` is WHICH node sits at that level right now.
 */
export interface ScopeNode {
  /** Stable level identity (`section`, `workshop`, `path:1`, …). */
  readonly key: string;
  /** Which node occupies that level (a slug, an urn, a route — the host's choice). */
  readonly id: string;
  /** Display text — carried so a result can be rendered without a second lookup. */
  readonly label: string;
  /** Route of this node, when it is navigable. */
  readonly url?: string;
}

/**
 * The host's answer to »does `node` still resolve under `context`?«.
 *
 * `context` is the new chain so far, outermost first, INCLUDING the switched
 * node — never the old one. Return the node as it exists under that context
 * (relabelled, re-routed — a step keeps its identity but gets the new workshop's
 * url), or `null` when it does not exist there.
 */
export type ScopeResolver = (
  node: ScopeNode,
  context: readonly ScopeNode[]
) => ScopeNode | null;

/** A switch: »in `chain`, at `index`, the person chose `choice`«. */
export interface ScopeSwitch {
  /** The chain as it is now, outermost first. */
  readonly chain: readonly ScopeNode[];
  /** Position of the crumb whose dropdown was used — the ORIGIN of the switch. */
  readonly index: number;
  /** The sibling that was chosen at that position. */
  readonly choice: ScopeNode;
  /** The host's knowledge. Absent ⇒ nothing following resolves (the safe default). */
  readonly resolve?: ScopeResolver;
}

/** What the switch leaves behind. */
export interface ScopeSwitchResult {
  /** The new chain: everything before the origin, the choice, then the survivors. */
  readonly chain: readonly ScopeNode[];
  /** The following nodes that survived, in order (empty ⇒ degraded to the origin). */
  readonly kept: readonly ScopeNode[];
  /** The following nodes that did not, in order — including those dropped by cascade. */
  readonly dropped: readonly ScopeNode[];
  /** Where to navigate: the deepest node of the new chain that has a route. */
  readonly url?: string;
}

/** Thrown when a switch names a position the chain does not have. */
export class ScopeSwitchError extends RangeError {
  constructor(index: number, length: number) {
    super(
      `[web-kit] scope switch at index ${index} — the chain has ${length} ` +
        `node(s); the origin must be one of them`
    );
    this.name = "ScopeSwitchError";
  }
}

/**
 * Apply the cascading resolution rule. Pure: it reads `chain` and asks `resolve`,
 * and returns a fresh chain — nothing is mutated.
 *
 * Ordering is the whole point, so it is guaranteed: `resolve` is called for the
 * following nodes outermost-first, and NOT AT ALL once one of them has dropped.
 */
export function resolveScopeSwitch({
  chain,
  index,
  choice,
  resolve,
}: ScopeSwitch): ScopeSwitchResult {
  if (!Number.isInteger(index) || index < 0 || index >= chain.length) {
    throw new ScopeSwitchError(index, chain.length);
  }
  const context: ScopeNode[] = [...chain.slice(0, index), choice];
  const kept: ScopeNode[] = [];
  const dropped: ScopeNode[] = [];
  let cascading = false;
  for (const node of chain.slice(index + 1)) {
    if (cascading) {
      // Its parent is already gone — there is no context left to ask about.
      dropped.push(node);
      continue;
    }
    const resolved = resolve?.(node, context) ?? null;
    if (resolved === null) {
      dropped.push(node);
      cascading = true;
      continue;
    }
    context.push(resolved);
    kept.push(resolved);
  }
  const url = deepestUrl(context);
  return { chain: context, kept, dropped, ...(url !== undefined ? { url } : {}) };
}

/** The route of the deepest node that has one — the target the switch lands on. */
function deepestUrl(chain: readonly ScopeNode[]): string | undefined {
  for (let i = chain.length - 1; i >= 0; i--) {
    const { url } = chain[i]!;
    if (url !== undefined) return url;
  }
  return undefined;
}
